import { useState } from "react";
import { imageUrl as buildImageUrl, useImageMeta } from "@/lib/images";

/**
 * Renders a word's card image — used by both the study card and the
 * word detail hero. Source precedence:
 *
 *   1. **`imageUrl`** — curator-authored, takes precedence. Attribution
 *      shown when supplied.
 *   2. **`imageQuery`** — English gloss; we build a URL against the
 *      on-demand image service (`apps/images` → Pexels) and let the
 *      browser fetch it. No attribution shown (Pexels doesn't require
 *      it).
 *   3. **Neither** — render the placeholder.
 *
 * The server already enforces "imageUrl set ⇒ imageQuery null" so the
 * two are effectively mutually exclusive, but the `??` keeps the
 * contract explicit on the client side too.
 *
 * Failure modes (404 from negative cache, network blip, 5xx) surface
 * via `<img onerror>`; we flip to the placeholder so the layout stays
 * put. The error state resets on every new (url, query) pair so
 * navigating between cards can't inherit the previous one's failure.
 *
 * Layout: parent `.fc-image` div locks a 16:10 aspect ratio so swapping
 * between image and placeholder doesn't shift the surrounding content.
 */
export function CardImage({
  imageUrl,
  imageQuery,
  attribution,
  alt,
  placeholderText = "no image",
}: {
  /** Curator-authored URL (from `Word.imageUrl`). Wins when set. */
  imageUrl: string | null;
  /** English gloss to feed `apps/images` when `imageUrl` is null. */
  imageQuery: string | null;
  /** Attribution shown only for curator URLs (Pexels doesn't require it). */
  attribution: string | null;
  alt: string;
  /** Text to show in the placeholder pill. Default: "no image". */
  placeholderText?: string;
}) {
  // Track which exact src has finished loading / errored. We DERIVE
  // the booleans `imageLoaded` / `errored` by comparing against the
  // currently-rendered src instead of using `useState`+`useEffect`-to-
  // reset. The reason: when card props change (mouse → duck), a
  // useState-driven `imageLoaded` is still `true` from the previous
  // card on the *first* render with the new query, because the reset
  // useEffect runs after render. That window is enough for the meta
  // hook gate to evaluate `true` and fire the metadata fetch BEFORE
  // the new image has even started loading — server returns 404 (no
  // sidecar yet), React Query caches the null result with
  // `staleTime: Infinity`, and the credit never appears for the rest
  // of the session. Derived state flips synchronously when src
  // changes, eliminating the window.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);

  const src = imageUrl ?? (imageQuery ? buildImageUrl(imageQuery) : null);
  const imageLoaded = src !== null && loadedSrc === src;
  const errored = src !== null && erroredSrc === src;
  const showImage = src !== null && !errored;

  // Pexels attribution. Only fetched when we're using the on-demand
  // service (imageQuery set, no curator imageUrl) — curator URLs use
  // the literal `attribution` prop instead.
  //
  // Gated on `imageLoaded` (derived above) to avoid a real race: on
  // the very first request for a new gloss, the image fetch triggers
  // Pexels search + download + sidecar write (all synchronous on the
  // server before the bytes return). If we fired the metadata fetch
  // in parallel, it would arrive at the server *before* the sidecar
  // is written and get 404'd — and React Query would then memoize
  // that null with `staleTime: Infinity`, so even after the sidecar
  // shows up the credit would never display. By waiting for the
  // image's `onLoad` (and tying it to the *current* src via the
  // comparison above) we know the sidecar is on disk before the
  // metadata fetch goes out.
  //
  // Side benefit: when an image errors (genuine "no image for query"
  // negative cache), `onLoad` never fires and we skip a doomed
  // metadata request.
  const { data: pexelsMeta } = useImageMeta(!imageUrl && imageQuery && imageLoaded ? imageQuery : null);

  return (
    <div className="fc-image">
      {showImage ? (
        <>
          <img
            // `key={src}` forces React to mount a fresh DOM node when
            // src changes. Without it, the browser shows the previous
            // card's image until the new one decodes — visually
            // alarming when transitions are slow (Pexels first-fetch
            // can be 500ms+). The remount drops back to the parent
            // `.fc-image` gradient placeholder during the load.
            key={src}
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setLoadedSrc(src)}
            onError={() => setErroredSrc(src)}
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* Curator attribution — plain text from Word.imageAttribution. */}
          {attribution && imageUrl && (
            <span className="absolute bottom-1 right-1.5 rounded bg-black/40 px-1.5 py-0.5 font-mono text-[9px] text-white/90 backdrop-blur-sm">
              {attribution}
            </span>
          )}
          {/* Pexels attribution — clickable link to the photo on Pexels.
              Format follows Pexels' recommended credit line: "Photo by
              [Name] on Pexels". target=_blank + noopener so leaving the
              app to view the photographer's page doesn't carry the
              session reference. */}
          {!imageUrl && pexelsMeta && (
            <a
              href={pexelsMeta.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-1 right-1.5 rounded bg-black/40 px-1.5 py-0.5 font-mono text-[9px] text-white/90 backdrop-blur-sm transition-colors hover:bg-black/60"
            >
              Photo by {pexelsMeta.photographer} on Pexels
            </a>
          )}
        </>
      ) : (
        <span className="rounded-md bg-white/55 px-2.5 py-1 font-mono text-[11px] backdrop-blur-md">
          {placeholderText}
        </span>
      )}
    </div>
  );
}
